import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subscription, first, interval } from 'rxjs';

import { BaseComponent } from 'src/app/components/base/base.component';
import { DriverEventType } from 'src/app/components/driver-events/enums/driver-event-type.enum';
import { DriverEvent } from 'src/app/components/driver-events/models/driver-event';
import { DriverEventsService } from '../../services/driver-events.service';
import { DriverEventResponse } from '../../models/api/driver-event.response';
import { DriverEventsConfirmationModalComponent } from '../driver-events-confirmation-modal/driver-events-confirmation-modal.component';
import { EnvironmentService } from 'src/app/services/environment.service';
import { GeolocationService } from 'src/app/components/driver-events/services/geolocation.service';
import { NoteType } from 'src/app/enums/note.enum';


@Component({
    templateUrl: './driver-events.component.html',
    styleUrls: [
        './../../../../../styles.scss',
        './driver-events.component.scss'
    ],
    encapsulation: ViewEncapsulation.ShadowDom
})
export class DriverEventsComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
    /** jayride booking ID */
    @Input() bookingId: string = '';
    /** booking public ID (used for authentication) */
    @Input() publicId: string = '';
    /** array of already triggered driver events to set initial state */
    @Input() storedDriverEvents!: DriverEventResponse[];
    /** starting local time when the driver events began to be accepted */
    @Input() enableStartTime?: Date | string;
    /** ending local time when the driver events began to be accepted */
    @Input() enableEndTime?: Date | string;

    /** output a driver event that was recently triggered */
    @Output() eventTriggered = new EventEmitter<DriverEvent>();

    NoteType = NoteType;

    /** events in a correct order */
    sequenceEvents: DriverEvent[] = [
        {
            type: DriverEventType.DriverDepartedToPickup,
            isDisabled: false,
            label: 'On My Way To Pickup',
            occurredAt: undefined
        },
        {
            type: DriverEventType.DriverArrivedAtPickup,
            isDisabled: true,
            label: 'Arrived At Pickup',
            occurredAt: undefined
        },
        {
            type: DriverEventType.DriverDepartedToDropoff,
            isDisabled: true,
            label: 'Passenger Picked Up',
            occurredAt: undefined
        },
        {
            type: DriverEventType.DriverArrivedAtDropoff,
            isDisabled: true,
            label: 'Passenger Dropped Off',
            occurredAt: undefined
        },
    ];
    noShowEvent: DriverEvent = {
        type: DriverEventType.DriverSubmittedCustomerNoShow,
        isDisabled: true,
        label: 'Passenger No Show',
        occurredAt: undefined
    };
    readonly eventTimeFormat = 'h:mma d MMM y';
    isGeolocationError = false;
    isTrackEventError = false;
    isTooEarlyToAcceptDriverEvents = false;
    isTooLateToAcceptDriverEvents = false;

    private acceptingDriverEventsIntervalSubscription?: Subscription;
    private readonly checkIfAcceptDriverEventsInterval = 1000 * 60; // 1 min

    constructor(
        private modalService: BsModalService,
        private geolocationService: GeolocationService,
        private driverEventsService: DriverEventsService,
        protected override environmentService: EnvironmentService
    ) { 
        super(environmentService);
    }

    ngOnInit() {
        this.setInitialState();
    }
    
    ngOnChanges(changes: SimpleChanges) {
		if (changes['customEnvironment']) {
            this.defineEnvironment();
        }
        if (changes['enableStartTime'] || changes['enableEndTime']) {
            this.defineIfAcceptDriverEvents();
        }
	}

    ngOnDestroy() {
        this.cleanIntervalCheck();
    }

    private setInitialState() {
        this.storedDriverEvents?.forEach(storedEvent => {
            if (storedEvent.eventType === DriverEventType.DriverSubmittedCustomerNoShow) {
                this.noShowEvent.isDisabled = true;
                this.noShowEvent.occurredAt = new Date(storedEvent.occurredAt);
            } else {
                const evt = this.sequenceEvents.find(e => e.type === storedEvent.eventType);
                if (evt) {
                    evt.isDisabled = true;
                    evt.occurredAt = new Date(storedEvent.occurredAt);
                }
            }
        });
        this.defineEventsState();
        this.defineIfAcceptDriverEvents();
    }

    private hideAllErrors() {
        this.isGeolocationError = false;
        this.isTrackEventError = false;
    }

    private defineEventsState() {
        if (this.noShowEvent.occurredAt) {
            this.noShowEvent.isDisabled = true;
            this.sequenceEvents.forEach(e => e.isDisabled = true);
        } else {
            // one button can be enabled at a time
            // if no stored events - enable the first
            // if Driver Arrived At Pickup -> enable next and the last (No Show)
            let nextBtnStateSet = false;
            this.sequenceEvents.map(e => {
                if(e.occurredAt) {
                    e.isDisabled = true;

                    if (e.type === DriverEventType.DriverArrivedAtPickup) { 
                        this.noShowEvent.isDisabled = false;
                    } else {
                        this.noShowEvent.isDisabled = true;
                    }
                } else {
                    if(!nextBtnStateSet) {
                        e.isDisabled = false;
                        nextBtnStateSet = true;
                    }
                }

            });
        }
    }

    private cleanIntervalCheck() {
        this.acceptingDriverEventsIntervalSubscription?.unsubscribe();
    }
    private inputLimitToDate(input: Date | string | undefined): Date | undefined {
        if (input) {
            return input instanceof Date ? input : new Date(input);
        } else return undefined;
    }
    private defineIfAcceptDriverEvents() {
        this.cleanIntervalCheck();
        const enableStartTime = this.inputLimitToDate(this.enableStartTime);
        const enableEndTime = this.inputLimitToDate(this.enableEndTime);

        this.acceptingDriverEventsIntervalSubscription = interval(this.checkIfAcceptDriverEventsInterval)
            .subscribe(res => {
                this.isTooEarlyToAcceptDriverEvents = this.driverEventsService.isTooEarlyToAcceptDriverEvents(enableStartTime);
                this.isTooLateToAcceptDriverEvents = this.driverEventsService.isTooLateToAcceptDriverEvents(enableEndTime);
            });
        this.isTooEarlyToAcceptDriverEvents = this.driverEventsService.isTooEarlyToAcceptDriverEvents(enableStartTime);
        this.isTooLateToAcceptDriverEvents = this.driverEventsService.isTooLateToAcceptDriverEvents(enableEndTime);
    }

    async triggerEvent(driverEvent?: DriverEvent) {
        if (!driverEvent) return;

        driverEvent.isDisabled = true;
        this.hideAllErrors();

        try {
            driverEvent.geolocation = await this.geolocationService.getCurrentLocation();
        } catch (err) {
            this.isGeolocationError = true;
            console.error(err);
        }

        this.driverEventsService.createDriverEvent(
            this.bookingId,
            this.publicId,
            driverEvent
        ).pipe(first()).subscribe(res => {
            driverEvent.occurredAt = new Date();
            this.defineEventsState();
            this.eventTriggered.emit(driverEvent);
        }, err => {
            driverEvent.isDisabled = false;
            driverEvent.occurredAt = undefined;
            this.isTrackEventError = true;
        });
    }

    getNotificationLabel(driverEventType: DriverEventType): string {
        switch (driverEventType) {
            case DriverEventType.DriverDepartedToPickup:
                return 'Driver departed to pickup';
            case DriverEventType.DriverArrivedAtPickup:
                return 'Driver arrived at pickup';
            case DriverEventType.DriverDepartedToDropoff:
                return 'Passenger picked up';
            case DriverEventType.DriverArrivedAtDropoff:
                return 'Passenger dropped off';
            case DriverEventType.DriverSubmittedCustomerNoShow:
                return 'Driver submitted passenger no show';
            default:
                return `'${DriverEventType.DriverDepartedToPickup}' event trigerred`;
        }
    }

    isEventDisabled(driverEvent?: DriverEvent): boolean {
        if (!driverEvent || 
            this.isTooEarlyToAcceptDriverEvents || 
            this.isTooLateToAcceptDriverEvents) return true;

        if (driverEvent.occurredAt) {
            return true;
        }
        return driverEvent.isDisabled;
    }

    openEventConfirmationModal(driverEvent: DriverEvent) {
        // FIXME: ngx-bootstrap is rendering modal container to DOM (expected to be in shadow DOM)
        const modalRef = this.modalService.show(DriverEventsConfirmationModalComponent, {
            initialState: {
                driverEvent: driverEvent
            }
        });
        
        modalRef?.content?.driverEventConfirmed.subscribe((driverEvent: DriverEvent) => {
            // disabled all other events
            if (driverEvent.type === DriverEventType.DriverSubmittedCustomerNoShow) {
                this.sequenceEvents.forEach(e => e.isDisabled = true);
            }

            this.triggerEvent(driverEvent);
        });
    }
}
